import type { TranslationKey } from "@privacy/translations";
import type { ComponentType, FC } from "react";
import Stack from "@mui/material/Stack";
import { Translate, useTranslation } from "@privacy/translations";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import type { IconWithBackgroundProps } from "../../../shared/icons";
import {
  RegistrationControllerIcon2,
  RegistrationControllerIcon1,
  InfoIcon,
  RegistrationProcessorIcon1,
  RegistrationProcessorIcon2,
} from "../../../shared/icons";
import type { ConntectedRegistrations, SystemById } from "../../../api/systems.types";
import { usePrivacyNavigate } from "../../../shared/usePrivacyNavigate";
import { Drawer } from "../../../shared/Drawer";
import { DynamicGrid } from "../../../dynamicGrid";
import { useHoverPopover } from "../../../shared/HoverPopover";
import type { RegistrationVariant } from "../../RegistrationsGridPage/RegistrationVariantProvider";
import { SystemsDrawerHeader } from "./SystemsDrawerHeader";
import { useGetSystemById } from "../../../api/systems";
import { MissingPermissionInfoPopover } from "../../../shared/MissingPermissionInfoPopover";

type ConnectedRegistrationsConfig = (system: SystemById) => {
  titleKey: TranslationKey;
  Icon1: ComponentType<IconWithBackgroundProps>;
  Icon2: ComponentType<IconWithBackgroundProps>;
  /** Does the system have more connected registrations than the user has permission to see */
  hasMoreConnectedRegistrations: boolean;
  connectedRegistrations: ConntectedRegistrations;
};

const connectedRegistrationsConfig: Record<RegistrationVariant, ConnectedRegistrationsConfig> = {
  Controller: system => ({
    titleKey: "SystemGrid.ConnectedControllerRegistrationHeader",
    Icon1: RegistrationControllerIcon1,
    Icon2: RegistrationControllerIcon2,
    hasMoreConnectedRegistrations:
      system.connectedControllerRegistrations.registrations.length < system.connectedControllerRegistrations.totalCount,
    connectedRegistrations: system.connectedControllerRegistrations,
  }),
  Processor: system => ({
    titleKey: "SystemGrid.ConnectedProcessorRegistrationHeader",
    Icon1: RegistrationProcessorIcon1,
    Icon2: RegistrationProcessorIcon2,
    hasMoreConnectedRegistrations:
      system.connectedProcessorRegistrations.registrations.length < system.connectedProcessorRegistrations.totalCount,
    connectedRegistrations: system.connectedProcessorRegistrations,
  }),
};

type ConnectedRegistrationsSubDrawerProps = { registrationVariant: RegistrationVariant };
export const ConnectedRegistrationsSubDrawer = ({ registrationVariant }: ConnectedRegistrationsSubDrawerProps) => {
  const systemId = DynamicGrid.useSelectedRowId();
  const systemQuery = useGetSystemById(systemId);
  const { t } = useTranslation();
  const { navigate, paths } = usePrivacyNavigate();

  return (
    <DynamicGrid.Drawer sx={{ pb: 0 }}>
      {(() => {
        if (!systemQuery.data) return <Drawer.Loading />;

        const system = systemQuery.data;
        const { titleKey, Icon1, Icon2, connectedRegistrations, hasMoreConnectedRegistrations } =
          connectedRegistrationsConfig[registrationVariant](system);

        return (
          <Stack sx={{ height: "100%" }}>
            <SystemsDrawerHeader
              activeTab="overview"
              statusIndicator="hidden"
              system={system}
              subDrawerHeader={
                <Drawer.SubDrawerHeader
                  onBackClick={() => navigate(paths.systemsInline(system.id))}
                  renderIcon={props => <Icon1 {...props} />}
                  title={t(titleKey)}
                  countAsterisk={hasMoreConnectedRegistrations}
                  count={connectedRegistrations.totalCount}
                />
              }
            />
            <Drawer.Grid
              getRowId={row => row.id}
              onRowClick={row =>
                navigate(paths.registrationsInline({ id: String(row.id), variant: registrationVariant }))
              }
              rows={connectedRegistrations.registrations}
              slots={{
                toolbar: Drawer.GridToolbar,
                noRowsOverlay: NoRowsOverlay,
              }}
              columns={[
                {
                  field: "name",
                  flex: 1,
                  headerName: t("SystemDrawer.SubDrawer.GridSystemName"),
                  valueGetter: (_, { name }) => name,
                  renderCell: ({ row }) => (
                    <Drawer.GridCell>
                      <Stack direction="row" sx={{ gap: 2, alignItems: "center" }}>
                        <Icon2 size="sm" />
                        <Stack>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: "600", lineHeight: 1.2, overflowWrap: "anywhere", hyphens: "auto" }}
                          >
                            {row.name}
                          </Typography>
                        </Stack>
                      </Stack>
                    </Drawer.GridCell>
                  ),
                },
              ]}
            />
            {hasMoreConnectedRegistrations ? (
              <Footer
                count={connectedRegistrations.registrations.length}
                totalCount={connectedRegistrations.totalCount}
              />
            ) : null}
          </Stack>
        );
      })()}
    </DynamicGrid.Drawer>
  );
};

const Footer: FC<{ totalCount: number; count: number }> = ({ totalCount, count }) => {
  const { popoverProps, anchorProps } = useHoverPopover();
  return (
    <Stack
      sx={[
        {
          pb: 1,
          pr: 1,
        },
        theme => ({ bgcolor: theme.palette.grey["100"] }),
      ]}
    >
      <Stack direction="row" sx={{ gap: 1, alignItems: "center", justifyContent: "end" }}>
        <Typography
          variant="body2"
          sx={[
            {
              fontSize: theme => theme.typography.pxToRem(12),
            },
            theme => ({ color: theme.palette.text.tertiary }),
          ]}
        >
          <Translate
            translationKey="SystemsDrawer.TotalSystems.Info"
            values={{
              count: (
                <Box component="span" sx={{ fontWeight: "bold" }}>
                  {count}
                </Box>
              ),
              totalCount: (
                <Box component="span" sx={{ fontWeight: "bold" }}>
                  {totalCount}
                </Box>
              ),
            }}
          />
        </Typography>
        <MissingPermissionInfoPopover popoverProps={popoverProps} />
        <Box {...anchorProps}>
          <InfoIcon color={theme => theme.palette.primary.main} strokeWidth={2.5} size={10} />
        </Box>
      </Stack>
    </Stack>
  );
};

const NoRowsOverlay = () => {
  return <Drawer.GridNoRowsOverlay labelKey="SystemsDrawer.SubDrawer.ConnectedRegistrationsEmptyState" />;
};
